/**
 * Per-item semantic validation of ONE capacity-rule payload — the shared
 * parseCapacityRules round-trip (drops malformed) + the bounds gate
 * (floor above ceiling deadlocks a pool and is refused everywhere).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { capacityBoundsIssue, parseCapacityRules } from '@trafficpulse/shared';

import type { CapacityRulePayload } from '../envelope/library-payload.types';
import type { ReviewIssue, ReviewItem } from './review-items';
import { finishItem } from './review-items';

export function validateCapacityRule(
  rule: CapacityRulePayload,
  index: number,
): ReviewItem {
  const parsed = parseCapacityRules([{ ...rule, id: rule.id ?? `c_${index}` }]);
  return finishItem({
    id: `cap_${index}`,
    kind: 'capacity-rule',
    label: rule.name?.trim() || `Capacity rule ${index + 1}`,
    capacityRule: parsed[0] ?? rule,
    issues: collectIssues(parsed),
  });
}

function collectIssues(parsed: ReturnType<typeof parseCapacityRules>): ReviewIssue[] {
  const issues: ReviewIssue[] = [];
  if (parsed.length === 0) {
    issues.push({
      level: 'error',
      message: 'Malformed capacity rule — needs layers and a max (or null).',
    });
    return issues;
  }
  const bounds = capacityBoundsIssue(parsed[0]);
  if (bounds) issues.push({ level: 'error', message: bounds });
  if (parsed[0].layers.some((layer) => layer !== 'paths')) {
    issues.push({
      level: 'warning',
      message:
        'Capacity beyond paths — AI capacity rules should cap the paths pool only.',
    });
  }
  return issues;
}
